"use client";

import React, { useState, useEffect } from "react";

interface ShareToolbarProps {
  title: string;
}

export function ShareToolbar({ title }: ShareToolbarProps) {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    const url = window.location.href;
    Promise.resolve().then(() => setShareUrl(url));
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to copy link: ", err);
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  return (
    <div className="flex items-center gap-2.5 my-2">
      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] flex items-center gap-1.5 mr-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--accent)]">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
        Share:
      </span>

      {/* Twitter / X */}
      <a
        href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--surface-muted)] text-[var(--ink-soft)] hover:text-[var(--accent)] transition-all duration-150 flex items-center justify-center cursor-pointer"
        title="Share on X (Twitter)"
        aria-label="Share on X (Twitter)"
      >
        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </a>

      {/* LinkedIn */}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--surface-muted)] text-[var(--ink-soft)] hover:text-[var(--accent)] transition-all duration-150 flex items-center justify-center cursor-pointer"
        title="Share on LinkedIn"
        aria-label="Share on LinkedIn"
      >
        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
          <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
        </svg>
      </a>

      {/* Copy Link */}
      <button
        onClick={handleCopy}
        className={`p-2 rounded border transition-all duration-150 flex items-center justify-center cursor-pointer ${
          copied
            ? "bg-[var(--accent-tint)] text-[var(--accent)] border-[var(--accent)]"
            : "bg-[var(--surface)] hover:bg-[var(--surface-muted)] text-[var(--ink-soft)] hover:text-[var(--accent)] border-[var(--border)] hover:border-[var(--accent)]"
        }`}
        title="Copy link to clipboard"
        aria-label="Copy link to clipboard"
      >
        {copied ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
        )}
      </button>

      {/* Pop-up confirmation */}
      <div
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded bg-[var(--surface)] border border-[var(--accent)] shadow-2xl transition-all duration-300 ease-out transform ${
          copied
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-4 opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="p-1.5 rounded bg-[var(--accent)] text-white flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <div className="flex flex-col text-left">
          <span className="text-sm font-bold text-[var(--ink)] leading-none mb-0.5 font-sans">
            Link Copied!
          </span>
          <span className="text-xs text-[var(--ink-soft)] leading-none font-sans">
            Ready to share.
          </span>
        </div>
      </div>
    </div>
  );
}
